import _ from 'lodash';
import { cornerSolutions } from 'domain/Options/generators/shapes';
import validateLimits from 'domain/Options/utils/validateLimits';
import { calculateFreestandingHobShapeMeasurementError } from 'pages/MainPage/CutoutsPage/freestandingHobMeasurementsError';
import messages from './messages';
import sinkPageMessages from '../SinkPage/messages';

function getRequiredLengthWithSink(sinkWidth) {
  return sinkWidth + (2 * 150);
}

export const getSinkLengthCondition = (sinkWidth) => (length) => {
  return length >= getRequiredLengthWithSink(sinkWidth);
};

const defaultCondition = () => true;

export const getSidesExcludingCorners = (shapeId, measurements) => {
  switch (shapeId) {
    case 'rectangular':
      return [
        { length: { name: 'a', value: measurements.a }, depth: { name: 'b', value: measurements.b }, hasCorner: false },
      ];
    case 'l-shape':
      return [
        { length: { name: 'a', value: measurements.a - measurements.d }, depth: { name: 'c', value: measurements.c }, hasCorner: true },
        { length: { name: 'b', value: measurements.b - measurements.c }, depth: { name: 'd', value: measurements.d }, hasCorner: true },
      ];
    case 'u-shape':
      return [
        { length: { name: 'a', value: measurements.a - measurements.e - measurements.f }, depth: { name: 'd', value: measurements.d }, hasCorner: true },
        { length: { name: 'b', value: measurements.b - measurements.d }, depth: { name: 'e', value: measurements.e }, hasCorner: true },
        { length: { name: 'c', value: measurements.c - measurements.d }, depth: { name: 'f', value: measurements.f }, hasCorner: true },
      ];
    case 'v-shape':
      return [
        { length: { name: 'a', value: measurements.a - cornerSolutions.deepCornerVshape }, depth: { name: 'c', value: measurements.c }, hasCorner: true },
        { length: { name: 'b', value: measurements.b - cornerSolutions.deepCornerVshape }, depth: { name: 'd', value: measurements.d }, hasCorner: true },
      ];
    case 'c-shape':
      return [
        { length: { name: 'a', value: measurements.a - cornerSolutions.deepCornerCshape }, depth: { name: 'c', value: measurements.c }, hasCorner: true },
        { length: { name: 'b', value: measurements.b - cornerSolutions.deepCornerCshape }, depth: { name: 'd', value: measurements.d }, hasCorner: true },
      ];
    case 'ii-shape':
      return [
        { length: { name: 'a', value: measurements.a }, depth: { name: 'b', value: measurements.b }, hasCorner: false },
        { length: { name: 'c', value: measurements.c }, depth: { name: 'd', value: measurements.d }, hasCorner: false },
      ];
    case 'g-shape':
        return [
          { length: { name: 'a', value: measurements.a - measurements.h - measurements.f }, depth: { name: 'e', value: measurements.e }, hasCorner: true },
          { length: { name: 'b', value: measurements.b - measurements.e - measurements.g }, depth: { name: 'f', value: measurements.f }, hasCorner: true },
          { length: { name: 'c', value: measurements.c - measurements.e }, depth: { name: 'h', value: measurements.h }, hasCorner: true },
          { length: { name: 'd', value: measurements.d - measurements.f }, depth: { name: 'g', value: measurements.g }, hasCorner: true },
        ];  
    case 'irregular':
      return null;
    default:
      throw new Error(`Unknown shape ${shapeId}`);
  }
};

export const validateSinkConditions = (shapeId, measurements, { depthCondition = defaultCondition, lengthCondition = defaultCondition }) => {
  const invalidLengths = [];
  const invalidDepths = [];
  const sides = getSidesExcludingCorners(shapeId, measurements);
  let invalidSideHasCorner = false;

  if (!sides) return null;

  const valid = sides.some((side) => {
    const validLength = lengthCondition(side.length.value);
    const validDepth = depthCondition ? depthCondition({ depth: side.depth.value }) : true;

    if (validLength && validDepth) return true;

    if (!validLength) invalidLengths.push(side.length.name);
    if (!validDepth) invalidDepths.push(side.depth.name);
    if (side.hasCorner) invalidSideHasCorner = true;
    return false;
  });

  if (valid) return null;

  return {
    lengths: invalidLengths,
    depths: invalidDepths,
    invalidSideHasCorner,
  };
};

function mustBeGreater(shape, fieldValues, f1, otherFields, errors) { 
  const otherTotal = otherFields.reduce((prev, f) => prev + (fieldValues[f] || shape.dimensions[f].defaultValue), 0);
  const fieldValue = fieldValues[f1] || shape.dimensions[f1].defaultValue;
  if (fieldValue <= otherTotal) {
    errors.push({
      fields: [f1, ...otherFields],
      message: messages.errorInvalidCombination,
    });
  }
}
function validateGshape(shape,fieldValues,measurementsWithOverhang, aSide, dSide,  bSide,  cSide, fSide, hSide, gSide, errors ){  
  let d;
  let c;
  let a = fieldValues[aSide] || shape.dimensions[aSide].defaultValue;
  let b = fieldValues[bSide] || shape.dimensions[bSide].defaultValue;
  if(measurementsWithOverhang){
    d = measurementsWithOverhang[dSide];
    c = measurementsWithOverhang[cSide]; 
  }else{
    d = fieldValues[dSide] || shape.dimensions[dSide].defaultValue;
    c = fieldValues[cSide] || shape.dimensions[cSide].defaultValue; 
  }
  let f = fieldValues[fSide] || shape.dimensions[fSide].defaultValue; 
  let h = fieldValues[hSide] || shape.dimensions[hSide].defaultValue ;
  let g = fieldValues[gSide] || shape.dimensions[gSide].defaultValue ;
  if((c >= b /* || b >= c */) &&  d >= a || (d >= a && (c >= b /* || b >= c */))){
    errors.push({
      fields: [aSide, bSide,  cSide,  dSide],
      message: messages.errorInvalidCombination,
    })
  }
  else if((d+h) >= a && c>= b){
    errors.push({
      fields: [ aSide, cSide, dSide, hSide ],
      message: messages.errorInvalidCombination,
    })
  }
  else if((f + h >= d) && (b === c) && !(d <= a)){
    errors.push({
      fields: [ dSide, fSide, hSide, cSide ],
      message: messages.errorInvalidCombination,
    })
  }else if (c + g >= b && (d+h) >= a){
    errors.push({
      fields: [ cSide,dSide, gSide,  hSide ],
      message: messages.errorInvalidCombination,
    })
  }
}

function minimumDifference(shape, fieldValues, f1, otherFields, minDiff, errors) {
  const otherTotal = otherFields.reduce((prev, f) => prev + (fieldValues[f] || shape.dimensions[f].defaultValue), 0);
  if (Math.abs((fieldValues[f1] || shape.dimensions[f1].defaultValue) - otherTotal) < minDiff) {
    errors.push({
      fields: [f1, ...otherFields],
      message: messages.errorInvalidCombination,
    });
  }
}

function buildSinkErrorMessage(invalidLength, invalidDepth, hasCorner, requiredLength, requiredDepth) {
  const errorMessage = [sinkPageMessages.pageTitle, ': '];
  if (invalidLength && invalidDepth) {
    errorMessage.push(
          { ...messages.sinkRequiredDepth, values: { depth: requiredDepth } },
          '. ',
          { ...messages.sinkMinimumLength, values: { length: requiredLength } },
        );
    if (hasCorner) {
      errorMessage.push(' ', { ...messages.lengthExcludingCorners }, '.');
    } else {
      errorMessage.push('.');
    }
  } else if (invalidDepth) {
    errorMessage.push({ ...messages.sinkRequiredDepth, values: { depth: requiredDepth } });
  } else {
    errorMessage.push({ ...messages.sinkMinimumLength, values: { length: requiredLength } });
    if (hasCorner) errorMessage.push(' ', { ...messages.lengthExcludingCorners });
  }

  return errorMessage;
}

export function calcShapeErrorMessages(shape, limits, fieldValues = {}, measurementsWithOverhang = null, sinkDepthCond = false, sinkWidth = 0, isFitBetween = false, freestandingHobs = null, freestandingHobDefaultWidth = null, useImperialUnits, measurementWithoutOverhang) {  
  const errors = [];
  // Add errors in order of importance.
  // ie. first error added will be the one shown first.
  // Validate values with min/max.
  errors.push(...validateLimits(fieldValues, shape.dimensions, limits, measurementsWithOverhang, useImperialUnits));
  // Add shape specific validations
  switch (shape.id) {
    case 'l-shape':
      mustBeGreater(shape, fieldValues, 'a', ['d'], errors);
      mustBeGreater(shape, fieldValues, 'b', ['c'], errors);
      break;
    case 'u-shape':
      mustBeGreater(shape, fieldValues, 'b', ['d'], errors);
      mustBeGreater(shape, fieldValues, 'c', ['d'], errors);
      mustBeGreater(shape, fieldValues, 'a', ['e', 'f'], errors);
      break;
    case 'g-shape':      
        mustBeGreater(shape, fieldValues, 'b', ['e', 'g'], errors);
        mustBeGreater(shape, fieldValues, 'd', [ 'f'], errors);
        mustBeGreater(shape, fieldValues, 'c', ['e'], errors);
        mustBeGreater(shape, fieldValues, 'a', ['f', 'h'], errors);
        validateGshape(shape, fieldValues ,measurementsWithOverhang, 'a', 'd',  'b',  'c', 'f', 'h','g' ,errors ) ;                                            
        break;
    case 'irregular':
      minimumDifference(shape, fieldValues, 'b', ['c'], limits.irregular, errors);
      break;
    default:
      break;
  }

  const sinkConditions = { depthCondition: sinkDepthCond };
  if (isFitBetween && sinkWidth) {
    sinkConditions.lengthCondition = getSinkLengthCondition(sinkWidth);
  }
  if (sinkConditions.depthCondition || sinkConditions.lengthCondition) {
    const measurements = _.mapValues(shape.dimensions, (dim, key) => fieldValues[key] || dim.defaultValue);
    const sinkErrors = validateSinkConditions(shape.id, measurements, sinkConditions);
    if (sinkErrors) {
      const {
        lengths,
        depths,
        invalidSideHasCorner,
      } = sinkErrors;
      errors.push({
        fields: [...sinkErrors.depths, ...sinkErrors.lengths],
        noLabel: true,
        message: buildSinkErrorMessage(lengths.length, depths.length, invalidSideHasCorner, getRequiredLengthWithSink(sinkWidth), sinkDepthCond && sinkDepthCond.depth),
        showSchematicDrawing: true,
      });
    }
  }
  if (freestandingHobs && freestandingHobs.get('value') > 0) {
    const measurements = _.mapValues(shape.dimensions, (dim, key) => fieldValues[key] || dim.defaultValue);
    const sides = getSidesExcludingCorners(shape.id, measurements);
    const freestandingHobErrors = calculateFreestandingHobShapeMeasurementError(freestandingHobs, sides, freestandingHobDefaultWidth, useImperialUnits);
    errors.push(...freestandingHobErrors);
  }
  return errors;
}
